import {
  findBranchContainingStep,
  isAssessTemplatedReportForCommentsStep,
  isChoiceStep,
  isFormChoiceStep,
  isParallelStep,
  isUserChoiceStep,
  isVariableChoiceStep,
} from "#src/batteries-included-components/Drawers/Workflows/WorkflowConfigurationDrawer/WorkflowConfigurationAddNodeDrawer.helpers";
import {
  AssessTemplatedReportForCommentsAction,
  AssessTemplatedReportForCommentsNextStepProperty,
  AssessTemplatedReportForCommentsNextStepPropertyType,
  ChoiceTask,
  FormChoiceAction,
  VariableChoiceAction,
  WorkflowConfigStepType,
  WorkflowConfigurationType,
  WorkflowParallelTasksType,
  WorkflowStepType,
} from "@validereinc/domain";
import { Workflows } from "@validereinc/domain-controllers";
import { Draft } from "immer";

const { getAllWorkflowSteps } = Workflows.logic;

const StepLocation = {
  TOP_LEVEL: "top-level",
  BRANCH: "branch",
} as const;
type StepLocationType = (typeof StepLocation)[keyof typeof StepLocation];

/**
 * Recursively searches for references to a step within a parallel branch's steps
 * This handles nested parallel branches at any depth
 */
const searchBranchForReferences = (
  stepIdToDelete: string,
  branch: {
    start: string;
    steps: Record<string, Draft<WorkflowConfigStepType>>;
  },
  parallelStep: Draft<WorkflowParallelTasksType>,
  parallelStepId: string,
  branchIndex: number,
  references: {
    viaNext: Array<{
      stepId: string;
      location: StepLocationType;
      branchInfo?: ReturnType<typeof findBranchContainingStep>;
    }>;
    viaChoices: Array<{
      stepId: string;
      choiceIndex: number;
      location: StepLocationType;
      branchInfo?: ReturnType<typeof findBranchContainingStep>;
    }>;
    viaAssessment: Array<{
      stepId: string;
      property: AssessTemplatedReportForCommentsNextStepPropertyType;
      location: StepLocationType;
      branchInfo?: ReturnType<typeof findBranchContainingStep>;
    }>;
    viaBranchStart: Array<{ parallelStepId: string; branchIndex: number }>;
  }
): void => {
  // Check branch start
  if (branch.start === stepIdToDelete) {
    references.viaBranchStart.push({
      parallelStepId,
      branchIndex,
    });
  }

  // Search within branch steps
  for (const [branchStepId, branchStep] of Object.entries(branch.steps)) {
    if (branchStep.next === stepIdToDelete) {
      references.viaNext.push({
        stepId: branchStepId,
        location: StepLocation.BRANCH,
        branchInfo: { parallelStep, branch, branchIndex },
      });
    }

    if (branchStep.type !== WorkflowStepType.PARALLEL) {
      if (isChoiceStep(branchStep)) {
        const task = branchStep.task as
          | ChoiceTask
          | FormChoiceAction
          | VariableChoiceAction;
        task.choices.forEach((choice, choiceIndex) => {
          if (choice.next === stepIdToDelete) {
            references.viaChoices.push({
              stepId: branchStepId,
              choiceIndex,
              location: StepLocation.BRANCH,
              branchInfo: { parallelStep, branch, branchIndex },
            });
          }
        });
      }

      if (isAssessTemplatedReportForCommentsStep(branchStep)) {
        const task = branchStep.task as AssessTemplatedReportForCommentsAction;
        if (task.assessment_success_next === stepIdToDelete) {
          references.viaAssessment.push({
            stepId: branchStepId,
            property:
              AssessTemplatedReportForCommentsNextStepProperty.ASSESSMENT_SUCCESS_NEXT,
            location: StepLocation.BRANCH,
            branchInfo: { parallelStep, branch, branchIndex },
          });
        }
        if (task.assessment_failure_next === stepIdToDelete) {
          references.viaAssessment.push({
            stepId: branchStepId,
            property:
              AssessTemplatedReportForCommentsNextStepProperty.ASSESSMENT_FAILURE_NEXT,
            location: StepLocation.BRANCH,
            branchInfo: { parallelStep, branch, branchIndex },
          });
        }
      }
    }

    // Recursively search nested parallel steps
    if (isParallelStep(branchStep)) {
      branchStep.branches.forEach((nestedBranch, nestedBranchIndex) => {
        // Recursively search the nested branch, passing the nested step's ID
        searchBranchForReferences(
          stepIdToDelete,
          nestedBranch,
          branchStep,
          branchStepId,
          nestedBranchIndex,
          references
        );
      });
    }
  }
};

/**
 * Finds all steps that reference a given step ID through various properties
 * Returns references grouped by location (top-level, branch, etc.)
 * Recursively searches through nested parallel branches at any depth
 */
const findAllReferencesToStep = (
  draft: Draft<WorkflowConfigurationType>,
  stepIdToDelete: string
): {
  viaNext: Array<{
    stepId: string;
    location: StepLocationType;
    branchInfo?: ReturnType<typeof findBranchContainingStep>;
  }>;
  viaChoices: Array<{
    stepId: string;
    choiceIndex: number;
    location: StepLocationType;
    branchInfo?: ReturnType<typeof findBranchContainingStep>;
  }>;
  viaAssessment: Array<{
    stepId: string;
    property: AssessTemplatedReportForCommentsNextStepPropertyType;
    location: StepLocationType;
    branchInfo?: ReturnType<typeof findBranchContainingStep>;
  }>;
  viaStart: boolean;
  viaBranchStart: Array<{ parallelStepId: string; branchIndex: number }>;
} => {
  const references = {
    viaNext: [] as Array<{
      stepId: string;
      location: StepLocationType;
      branchInfo?: ReturnType<typeof findBranchContainingStep>;
    }>,
    viaChoices: [] as Array<{
      stepId: string;
      choiceIndex: number;
      location: StepLocationType;
      branchInfo?: ReturnType<typeof findBranchContainingStep>;
    }>,
    viaAssessment: [] as Array<{
      stepId: string;
      property: AssessTemplatedReportForCommentsNextStepPropertyType;
      location: StepLocationType;
      branchInfo?: ReturnType<typeof findBranchContainingStep>;
    }>,
    viaStart: draft.start === stepIdToDelete,
    viaBranchStart: [] as Array<{
      parallelStepId: string;
      branchIndex: number;
    }>,
  };

  // Search top-level steps
  for (const [stepId, step] of Object.entries(draft.steps)) {
    if (step.next === stepIdToDelete) {
      references.viaNext.push({ stepId, location: StepLocation.TOP_LEVEL });
    }

    if (step.type !== WorkflowStepType.PARALLEL) {
      if (isChoiceStep(step)) {
        const task = step.task as
          | ChoiceTask
          | FormChoiceAction
          | VariableChoiceAction;
        task.choices.forEach((choice, index) => {
          if (choice.next === stepIdToDelete) {
            references.viaChoices.push({
              stepId,
              choiceIndex: index,
              location: StepLocation.TOP_LEVEL,
            });
          }
        });
      }

      if (isAssessTemplatedReportForCommentsStep(step)) {
        const task = step.task as AssessTemplatedReportForCommentsAction;
        if (task.assessment_success_next === stepIdToDelete) {
          references.viaAssessment.push({
            stepId,
            property:
              AssessTemplatedReportForCommentsNextStepProperty.ASSESSMENT_SUCCESS_NEXT,
            location: StepLocation.TOP_LEVEL,
          });
        }
        if (task.assessment_failure_next === stepIdToDelete) {
          references.viaAssessment.push({
            stepId,
            property:
              AssessTemplatedReportForCommentsNextStepProperty.ASSESSMENT_FAILURE_NEXT,
            location: StepLocation.TOP_LEVEL,
          });
        }
      }
    }

    // Search parallel branches recursively
    if (isParallelStep(step)) {
      step.branches.forEach((branch, branchIndex) => {
        searchBranchForReferences(
          stepIdToDelete,
          branch,
          step,
          stepId,
          branchIndex,
          references
        );
      });
    }
  }

  return references;
};

/**
 * Determines if a step is top-level, inside a parallel branch, or is the workflow start step
 */
const findStepLocation = (
  draft: Draft<WorkflowConfigurationType>,
  stepId: string
): {
  isTopLevel: boolean;
  isStart: boolean;
  branchInfo: ReturnType<typeof findBranchContainingStep> | null;
} => {
  const isStart = draft.start === stepId;
  const branchInfo = findBranchContainingStep(draft, stepId);
  const isTopLevel = !branchInfo && Boolean(draft.steps[stepId]);

  return {
    isTopLevel,
    isStart,
    branchInfo,
  };
};

/**
 * Updates the next and end references for a referencing step
 */
const updateNextAndEndReferencesForReferencingStep = (
  step: Draft<WorkflowConfigStepType>,
  isEnd: boolean,
  nextStepId: string | undefined
) => {
  if (step) {
    if (isEnd) {
      step.end = true;
      step.next = undefined;
    } else if (nextStepId) {
      step.next = nextStepId;
    } else {
      step.end = true;
      step.next = undefined;
    }
  }
};

/**
 * Removes all choices that reference the deleted step from a choice array
 */
const removeReferencesToDeletedStepInChoiceArray = (
  step: Draft<Exclude<WorkflowConfigStepType, WorkflowParallelTasksType>>,
  stepIdToDelete: string
) => {
  if (isUserChoiceStep(step)) {
    const task = step.task as ChoiceTask;
    task.choices = task.choices.filter(
      (choice) => choice.next !== stepIdToDelete
    );
  } else if (isFormChoiceStep(step)) {
    const task = step.task as FormChoiceAction;
    task.choices = task.choices.filter(
      (choice) => choice.next !== stepIdToDelete
    );
  } else if (isVariableChoiceStep(step)) {
    const task = step.task as VariableChoiceAction;
    task.choices = task.choices.filter(
      (choice) => choice.next !== stepIdToDelete
    );
  }
};

/**
 * Updates a reference in an assess templated report for comments step
 */
const updateReferenceInAssessTemplatedReportForCommentsStep = (
  step: Draft<WorkflowConfigStepType>,
  isEnd: boolean,
  nextStepId: string | undefined,
  reference: {
    property: AssessTemplatedReportForCommentsNextStepPropertyType;
    location: StepLocationType;
    branchInfo?: ReturnType<typeof findBranchContainingStep>;
  }
) => {
  if (
    step &&
    step.type !== WorkflowStepType.PARALLEL &&
    isAssessTemplatedReportForCommentsStep(step)
  ) {
    const task = step.task as AssessTemplatedReportForCommentsAction;
    if (isEnd || !nextStepId) {
      delete task[reference.property];
    } else {
      task[reference.property] = nextStepId;
    }
  }
};

/**
 * Updates all references to a deleted step, connecting previous steps to next steps
 */
const updateReferencesAfterDeletion = (
  draft: Draft<WorkflowConfigurationType>,
  stepIdToDelete: string,
  nextStepId: string | undefined,
  isEnd: boolean,
  references: ReturnType<typeof findAllReferencesToStep>
): void => {
  // Update workflow start
  if (references.viaStart && nextStepId) {
    draft.start = nextStepId;
  }

  // Update references via next
  references.viaNext.forEach((reference) => {
    if (reference.location === StepLocation.TOP_LEVEL) {
      const referencingStep = draft.steps[reference.stepId];
      updateNextAndEndReferencesForReferencingStep(
        referencingStep,
        isEnd,
        nextStepId
      );
    } else if (reference.branchInfo) {
      const branchStep = reference.branchInfo.branch.steps[reference.stepId];
      updateNextAndEndReferencesForReferencingStep(
        branchStep,
        isEnd,
        nextStepId
      );
    }
  });

  // Group choice references by stepId to process each step once
  // This avoids index shifting issues when multiple choices in the same step reference the deleted step
  const choiceReferencesByStepId = new Map<
    string,
    Array<{
      location: StepLocationType;
      branchInfo?: ReturnType<typeof findBranchContainingStep>;
    }>
  >();

  references.viaChoices.forEach((reference) => {
    const existing = choiceReferencesByStepId.get(reference.stepId) ?? [];
    existing.push({
      location: reference.location,
      branchInfo: reference.branchInfo,
    });
    choiceReferencesByStepId.set(reference.stepId, existing);
  });

  // Process each step once, filtering out all choices that reference the deleted step
  choiceReferencesByStepId.forEach((_, stepId) => {
    const firstReference = references.viaChoices.find(
      (ref) => ref.stepId === stepId
    );
    if (!firstReference) return;

    if (firstReference.location === StepLocation.TOP_LEVEL) {
      const referencingStep = draft.steps[stepId];
      if (
        referencingStep &&
        referencingStep.type !== WorkflowStepType.PARALLEL
      ) {
        removeReferencesToDeletedStepInChoiceArray(
          referencingStep,
          stepIdToDelete
        );
      }
    } else if (firstReference.branchInfo) {
      const branchStep = firstReference.branchInfo.branch.steps[stepId];
      if (branchStep && branchStep.type !== WorkflowStepType.PARALLEL) {
        removeReferencesToDeletedStepInChoiceArray(branchStep, stepIdToDelete);
      }
    }
  });

  references.viaAssessment.forEach((reference) => {
    if (reference.location === StepLocation.TOP_LEVEL) {
      const step = draft.steps[reference.stepId];
      updateReferenceInAssessTemplatedReportForCommentsStep(
        step,
        isEnd,
        nextStepId,
        reference
      );
    } else if (reference.branchInfo) {
      const branchStep = reference.branchInfo.branch.steps[reference.stepId];
      updateReferenceInAssessTemplatedReportForCommentsStep(
        branchStep,
        isEnd,
        nextStepId,
        reference
      );
    }
  });

  references.viaBranchStart.forEach((reference) => {
    const parallelStep = draft.steps[reference.parallelStepId];
    if (parallelStep && isParallelStep(parallelStep)) {
      const branch = parallelStep.branches[reference.branchIndex];
      if (branch) {
        if (isEnd) {
          // Find the first step in the branch that doesn't reference the deleted step
          const firstValidStepId = Object.keys(branch.steps).find(
            (stepId) =>
              stepId !== stepIdToDelete &&
              branch.steps[stepId].next !== stepIdToDelete
          );
          if (firstValidStepId) {
            branch.start = firstValidStepId;
          } else {
            // Branch is empty or all steps reference deleted step, remove branch
            parallelStep.branches.splice(reference.branchIndex, 1);
          }
        } else if (nextStepId) {
          branch.start = nextStepId;
        } else {
          // Similar logic for when nextStepId is undefined
          const firstValidStepId = Object.keys(branch.steps).find(
            (stepId) =>
              stepId !== stepIdToDelete &&
              branch.steps[stepId].next !== stepIdToDelete
          );
          if (firstValidStepId) {
            branch.start = firstValidStepId;
          } else {
            parallelStep.branches.splice(reference.branchIndex, 1);
          }
        }
      }
    }
  });
};

/**
 * Checks if a step is in a choice branch that cannot be safely deleted
 * Returns true if the step is in a protected branch (has external references)
 */
const isStepInProtectedChoiceBranch = (
  draft: Draft<WorkflowConfigurationType>,
  stepIdToDelete: string,
  references: ReturnType<typeof findAllReferencesToStep>
): boolean => {
  if (references.viaChoices.length === 0) {
    return false;
  }

  for (const choiceRef of references.viaChoices) {
    const choiceStepId = choiceRef.stepId;
    const allSteps = getAllWorkflowSteps(draft.steps);
    const choiceStep = allSteps[choiceStepId];

    if (!choiceStep || !isChoiceStep(choiceStep)) {
      continue;
    }

    const task = choiceStep.task as
      | ChoiceTask
      | FormChoiceAction
      | VariableChoiceAction;

    // Find which choice references this step
    for (const choice of task.choices) {
      if (choice.next === stepIdToDelete) {
        // This is the branch containing the step to delete
        // Check if this branch can be deleted
        const branchSteps = collectStepsInLinearChain(
          draft as unknown as WorkflowConfigurationType,
          choice.next
        );

        // Check if any step in this branch has external references
        for (const branchStepId of branchSteps) {
          if (
            checkHasExternalReferences(
              draft as unknown as WorkflowConfigurationType,
              branchStepId,
              choiceStepId,
              branchSteps,
              choice.next
            )
          ) {
            // This branch has external references, so it's protected
            return true;
          }
        }
      }
    }
  }

  return false;
};

/**
 * Deletes a regular step (only references other steps through "end" and "next" properties) and updates references
 */
const deleteRegularStep = (
  draft: Draft<WorkflowConfigurationType>,
  stepIdToDelete: string,
  step: Draft<WorkflowConfigStepType>,
  location: ReturnType<typeof findStepLocation>,
  references: ReturnType<typeof findAllReferencesToStep>
): void => {
  const nextStepId = step.next;
  const isEnd = step.end ?? false;

  // If the step is in a protected choice branch (has external references), don't delete it
  if (isStepInProtectedChoiceBranch(draft, stepIdToDelete, references)) {
    return;
  }

  if (references.viaChoices.length > 0) {
    const deletedStepIds = new Set<string>();
    deleteStepAndReferencedSteps(draft, stepIdToDelete, deletedStepIds);
  } else {
    if (location.isTopLevel) {
      delete draft.steps[stepIdToDelete];
    } else if (location.branchInfo) {
      delete location.branchInfo.branch.steps[stepIdToDelete];
    }
  }

  updateReferencesAfterDeletion(
    draft,
    stepIdToDelete,
    nextStepId,
    isEnd,
    references
  );
};

/**
 * Collects all step IDs referenced by choices in a choice step
 */
const collectChoiceStepIds = (
  step: Draft<WorkflowConfigStepType>
): string[] => {
  const stepIds: string[] = [];

  if (isParallelStep(step)) {
    return stepIds;
  }

  if (isChoiceStep(step)) {
    const task = step.task as
      | ChoiceTask
      | FormChoiceAction
      | VariableChoiceAction;
    task.choices.forEach((choice) => {
      if (choice.next) {
        stepIds.push(choice.next);
      }
    });
  }

  return stepIds;
};

/**
 * Recursively collects all step IDs following a given step via `next` properties.
 * This is used to determine which steps should be deleted when a step referenced
 * in a choices array is deleted.
 */
const collectStepsInChain = (
  draft: Draft<WorkflowConfigurationType>,
  stepId: string,
  collectedStepIds: Set<string> = new Set()
): Set<string> => {
  if (collectedStepIds.has(stepId)) {
    return collectedStepIds;
  }

  const allSteps = getAllWorkflowSteps(draft.steps);
  const step = allSteps[stepId];
  if (!step) {
    return collectedStepIds;
  }

  collectedStepIds.add(stepId);

  if (step.next) {
    collectStepsInChain(draft, step.next, collectedStepIds);
  }

  return collectedStepIds;
};

/**
 * Recursively deletes a step and all steps it references (for choice steps)
 */
const deleteStepAndReferencedSteps = (
  draft: Draft<WorkflowConfigurationType>,
  stepId: string,
  deletedStepIds: Set<string>
): void => {
  if (deletedStepIds.has(stepId)) {
    return;
  }

  const allSteps = getAllWorkflowSteps(draft.steps);
  const step = allSteps[stepId];
  if (!step) {
    return;
  }

  deletedStepIds.add(stepId);

  const referencedStepIds: string[] = [];

  if (step.next) {
    referencedStepIds.push(step.next);
  }

  if (step.type !== WorkflowStepType.PARALLEL) {
    if (isChoiceStep(step)) {
      referencedStepIds.push(...collectChoiceStepIds(step));
    }

    if (isAssessTemplatedReportForCommentsStep(step)) {
      const task = step.task as AssessTemplatedReportForCommentsAction;
      if (task.assessment_success_next) {
        referencedStepIds.push(task.assessment_success_next);
      }
      if (task.assessment_failure_next) {
        referencedStepIds.push(task.assessment_failure_next);
      }
    }
  }

  if (isParallelStep(step)) {
    step.branches.forEach((branch) => {
      Object.keys(branch.steps).forEach((branchStepId) => {
        referencedStepIds.push(branchStepId);
      });
    });
  }

  referencedStepIds.forEach((referencedStepId) => {
    deleteStepAndReferencedSteps(draft, referencedStepId, deletedStepIds);
  });

  const location = findStepLocation(draft, stepId);
  if (location.isTopLevel) {
    delete draft.steps[stepId];
  } else if (location.branchInfo) {
    delete location.branchInfo.branch.steps[stepId];
  }
};

/**
 * Checks if any step in a branch references the choice step being deleted
 * This indicates a circular reference, so the branch should not be deleted
 */
const branchReferencesChoiceStep = (
  draft: Draft<WorkflowConfigurationType>,
  branchStepIds: Set<string>,
  choiceStepId: string
): boolean => {
  const allSteps = getAllWorkflowSteps(draft.steps);

  for (const branchStepId of branchStepIds) {
    const branchStep = allSteps[branchStepId];
    if (!branchStep) {
      continue;
    }

    // Check if this step's next property references the choice step
    if (branchStep.next === choiceStepId) {
      return true;
    }

    // Check if this step is a choice step that references the choice step being deleted
    if (isChoiceStep(branchStep)) {
      const task = branchStep.task as
        | ChoiceTask
        | FormChoiceAction
        | VariableChoiceAction;
      if (task.choices.some((choice) => choice.next === choiceStepId)) {
        return true;
      }
    }

    // Check if this step is an assess templated report step that references the choice step
    if (
      branchStep.type !== WorkflowStepType.PARALLEL &&
      isAssessTemplatedReportForCommentsStep(branchStep)
    ) {
      const task = branchStep.task as AssessTemplatedReportForCommentsAction;
      if (
        task.assessment_success_next === choiceStepId ||
        task.assessment_failure_next === choiceStepId
      ) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Recursively checks if a step can reach the target step through any path (next, choices, etc.)
 * Used to detect if a branch step can reach the workflow start
 */
const canReachStep = (
  config: WorkflowConfigurationType,
  fromStepId: string,
  targetStepId: string,
  visited: Set<string> = new Set()
): boolean => {
  if (fromStepId === targetStepId) {
    return true;
  }

  if (visited.has(fromStepId)) {
    return false; // Cycle detected, but not the target
  }

  visited.add(fromStepId);

  const allSteps = getAllWorkflowSteps(config.steps);
  const step = allSteps[fromStepId];
  if (!step) {
    return false;
  }

  // Check next property
  if (step.next) {
    if (canReachStep(config, step.next, targetStepId, new Set(visited))) {
      return true;
    }
  }

  // Check choices
  if (isChoiceStep(step)) {
    const task = step.task as
      | ChoiceTask
      | FormChoiceAction
      | VariableChoiceAction;
    for (const choice of task.choices) {
      if (choice.next) {
        if (canReachStep(config, choice.next, targetStepId, new Set(visited))) {
          return true;
        }
      }
    }
  }

  // Check assessment properties
  if (
    step.type !== WorkflowStepType.PARALLEL &&
    isAssessTemplatedReportForCommentsStep(step)
  ) {
    const task = step.task as AssessTemplatedReportForCommentsAction;
    if (task.assessment_success_next) {
      if (
        canReachStep(
          config,
          task.assessment_success_next,
          targetStepId,
          new Set(visited)
        )
      ) {
        return true;
      }
    }
    if (task.assessment_failure_next) {
      if (
        canReachStep(
          config,
          task.assessment_failure_next,
          targetStepId,
          new Set(visited)
        )
      ) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Determines which branches of a choice step can be safely deleted
 * Returns a set of choice.next step IDs that can be deleted (branches without external references)
 * Never deletes branches that contain the workflow start step or create circular references
 */
const getBranchesToDelete = (
  draft: Draft<WorkflowConfigurationType>,
  choiceStepId: string,
  step: Draft<WorkflowConfigStepType>
): Set<string> => {
  if (!isChoiceStep(step)) {
    return new Set();
  }

  const task = step.task as
    | ChoiceTask
    | FormChoiceAction
    | VariableChoiceAction;

  const branchesToDelete = new Set<string>();
  const workflowStart = draft.start;

  // For each choice, check if its branch can be deleted
  for (const choice of task.choices) {
    if (!choice.next) {
      continue;
    }

    // Collect all steps in this branch (linear chain)
    const branchSteps = collectStepsInLinearChain(
      draft as unknown as WorkflowConfigurationType,
      choice.next
    );

    // Never delete a branch that contains the workflow start step
    if (workflowStart && branchSteps.has(workflowStart)) {
      continue;
    }

    // Never delete a branch that can reach the workflow start step (even indirectly)
    if (workflowStart) {
      let canReachStart = false;
      for (const branchStepId of branchSteps) {
        if (
          canReachStep(
            draft as unknown as WorkflowConfigurationType,
            branchStepId,
            workflowStart
          )
        ) {
          canReachStart = true;
          break;
        }
      }
      if (canReachStart) {
        continue;
      }
    }

    // Never delete a branch that creates a circular reference (references the choice step being deleted)
    if (branchReferencesChoiceStep(draft, branchSteps, choiceStepId)) {
      continue;
    }

    // Check if any step in this branch has external references
    // Pass the current choice's next to identify references from other choices
    let hasExternalRef = false;
    for (const branchStepId of branchSteps) {
      if (
        checkHasExternalReferences(
          draft as unknown as WorkflowConfigurationType,
          branchStepId,
          choiceStepId,
          branchSteps,
          choice.next
        )
      ) {
        hasExternalRef = true;
        break;
      }
    }

    // If no external references, this branch can be deleted
    if (!hasExternalRef) {
      branchesToDelete.add(choice.next);
    }
  }

  return branchesToDelete;
};

/**
 * Deletes a choice step (e.g. user choice, form choice, etc.) and all steps referenced by its choices
 * Only deletes branches that don't contain nodes referenced outside of the linear branch
 * If no branches can be deleted (all are protected), the choice step is not deleted to prevent breaking the workflow
 */
const deleteChoiceStep = (
  draft: Draft<WorkflowConfigurationType>,
  stepIdToDelete: string,
  step: Draft<WorkflowConfigStepType>,
  location: ReturnType<typeof findStepLocation>,
  references: ReturnType<typeof findAllReferencesToStep>
): void => {
  const nextStepId = step.next;
  const isEnd = step.end ?? false;

  // Determine which branches can be safely deleted
  const branchesToDelete = getBranchesToDelete(draft, stepIdToDelete, step);

  // Count how many choices have next properties (branches that could potentially be deleted)
  let choicesWithNext = 0;
  if (isChoiceStep(step)) {
    const task = step.task as
      | ChoiceTask
      | FormChoiceAction
      | VariableChoiceAction;
    choicesWithNext = task.choices.filter((choice) => choice.next).length;
  }

  // If there are branches (choices with next properties) but none can be deleted,
  // it means they're all protected (have external references, circular refs, or reach the start)
  // In this case, we should not delete the choice step to prevent breaking the workflow
  // However, if all branches are deletable (branchesToDelete.size === choicesWithNext),
  // or if there are no branches at all (choicesWithNext === 0), we should allow deletion
  if (branchesToDelete.size === 0 && choicesWithNext > 0) {
    // There are branches, but none can be deleted - they're all protected
    return; // Don't delete the choice step if all branches are protected
  }

  // Delete steps in branches that can be safely deleted
  const deletedStepIds = new Set<string>([stepIdToDelete]);
  for (const branchStartStepId of branchesToDelete) {
    deleteStepAndReferencedSteps(draft, branchStartStepId, deletedStepIds);
  }

  // Remove choices that reference deleted branches
  let remainingChoicesCount = 0;
  if (isChoiceStep(step)) {
    const task = step.task as
      | ChoiceTask
      | FormChoiceAction
      | VariableChoiceAction;

    // Filter out choices that reference deleted branches
    if (isUserChoiceStep(step)) {
      const choiceTask = task as ChoiceTask;
      choiceTask.choices = choiceTask.choices.filter(
        (choice) => !choice.next || !branchesToDelete.has(choice.next)
      );
      remainingChoicesCount = choiceTask.choices.length;
    } else if (isFormChoiceStep(step)) {
      const formChoiceTask = task as FormChoiceAction;
      formChoiceTask.choices = formChoiceTask.choices.filter(
        (choice) => !choice.next || !branchesToDelete.has(choice.next)
      );
      remainingChoicesCount = formChoiceTask.choices.length;
    } else if (isVariableChoiceStep(step)) {
      const variableChoiceTask = task as VariableChoiceAction;
      variableChoiceTask.choices = variableChoiceTask.choices.filter(
        (choice) => !choice.next || !branchesToDelete.has(choice.next)
      );
      remainingChoicesCount = variableChoiceTask.choices.length;
    }
  }

  // Only update references and delete the choice step if all choices have been removed
  // If there are remaining choices, keep the choice step and don't update references to it
  if (remainingChoicesCount === 0) {
    updateReferencesAfterDeletion(
      draft,
      stepIdToDelete,
      nextStepId,
      isEnd,
      references
    );

    if (location.isTopLevel) {
      delete draft.steps[stepIdToDelete];
    } else if (location.branchInfo) {
      delete location.branchInfo.branch.steps[stepIdToDelete];
    }
  }
};

/**
 * Deletes a parallel step and all steps within all its branches
 */
const deleteParallelStep = (
  draft: Draft<WorkflowConfigurationType>,
  stepIdToDelete: string,
  step: Draft<WorkflowParallelTasksType>,
  location: ReturnType<typeof findStepLocation>,
  references: ReturnType<typeof findAllReferencesToStep>
): void => {
  const nextStepId = step.next;
  const isEnd = step.end ?? false;

  const deletedStepIds = new Set<string>([stepIdToDelete]);
  step.branches.forEach((branch) => {
    Object.keys(branch.steps).forEach((branchStepId) => {
      deleteStepAndReferencedSteps(draft, branchStepId, deletedStepIds);
    });
  });

  updateReferencesAfterDeletion(
    draft,
    stepIdToDelete,
    nextStepId,
    isEnd,
    references
  );

  if (location.isTopLevel) {
    delete draft.steps[stepIdToDelete];
  } else if (location.branchInfo) {
    delete location.branchInfo.branch.steps[stepIdToDelete];
  }
};

/**
 * Checks if a step deletion will be prevented (returns true if deletion will be prevented)
 */
const willDeletionBePrevented = (
  draft: Draft<WorkflowConfigurationType>,
  stepIdToDelete: string,
  step: Draft<WorkflowConfigStepType>,
  references: ReturnType<typeof findAllReferencesToStep>
): boolean => {
  // Check if it's a regular step in a protected choice branch
  if (!isChoiceStep(step) && !isParallelStep(step)) {
    if (isStepInProtectedChoiceBranch(draft, stepIdToDelete, references)) {
      return true;
    }
  }

  // Check if it's a choice step with all branches protected
  if (isChoiceStep(step)) {
    const branchesToDelete = getBranchesToDelete(draft, stepIdToDelete, step);
    const task = step.task as
      | ChoiceTask
      | FormChoiceAction
      | VariableChoiceAction;
    const choicesWithNext = task.choices.filter((choice) => choice.next).length;

    // If there are branches but none can be deleted, deletion will be prevented
    if (branchesToDelete.size === 0 && choicesWithNext > 0) {
      return true;
    }
  }

  return false;
};

/**
 * Deletes a step from within a parallel branch
 */
const deleteStepFromBranch = (
  draft: Draft<WorkflowConfigurationType>,
  stepIdToDelete: string,
  step: Draft<WorkflowConfigStepType>,
  branchInfo: NonNullable<ReturnType<typeof findBranchContainingStep>>,
  references: ReturnType<typeof findAllReferencesToStep>
): void => {
  const nextStepId = step.next;
  const isEnd = step.end ?? false;
  const isBranchStart = branchInfo.branch.start === stepIdToDelete;

  // Check if deletion will be prevented BEFORE updating branch.start
  if (willDeletionBePrevented(draft, stepIdToDelete, step, references)) {
    // Deletion will be prevented, don't modify branch.start
    return;
  }

  // Update branch start if deleting the start step
  // Only do this if we know deletion will proceed
  if (isBranchStart) {
    if (isEnd) {
      // Find first valid step in branch
      const firstValidStepId = Object.keys(branchInfo.branch.steps).find(
        (stepId) => stepId !== stepIdToDelete
      );
      if (firstValidStepId) {
        branchInfo.branch.start = firstValidStepId;
      } else {
        // Branch is empty, remove it
        branchInfo.parallelStep.branches.splice(branchInfo.branchIndex, 1);
        return;
      }
    } else if (nextStepId && branchInfo.branch.steps[nextStepId]) {
      branchInfo.branch.start = nextStepId;
    } else {
      // Find first valid step in branch
      const firstValidStepId = Object.keys(branchInfo.branch.steps).find(
        (stepId) => stepId !== stepIdToDelete
      );
      if (firstValidStepId) {
        branchInfo.branch.start = firstValidStepId;
      } else {
        branchInfo.parallelStep.branches.splice(branchInfo.branchIndex, 1);
        return;
      }
    }
  }

  if (isChoiceStep(step)) {
    deleteChoiceStep(
      draft,
      stepIdToDelete,
      step,
      { isTopLevel: false, isStart: false, branchInfo },
      references
    );
  } else if (isParallelStep(step)) {
    deleteParallelStep(
      draft,
      stepIdToDelete,
      step,
      { isTopLevel: false, isStart: false, branchInfo },
      references
    );
  } else {
    deleteRegularStep(
      draft,
      stepIdToDelete,
      step,
      { isTopLevel: false, isStart: false, branchInfo },
      references
    );
  }
};

/**
 * Collects all step IDs in a linear chain starting from a given step ID
 * Follows the `next` property until it reaches the end or a step with no next
 */
const collectStepsInLinearChain = (
  config: WorkflowConfigurationType,
  startStepId: string,
  collectedStepIds: Set<string> = new Set()
): Set<string> => {
  if (collectedStepIds.has(startStepId)) {
    return collectedStepIds;
  }

  const allSteps = getAllWorkflowSteps(config.steps);
  const step = allSteps[startStepId];
  if (!step) {
    return collectedStepIds;
  }

  collectedStepIds.add(startStepId);

  // Only follow linear chains (next property), not choices or parallel branches
  if (step.next && !step.end) {
    collectStepsInLinearChain(config, step.next, collectedStepIds);
  }

  return collectedStepIds;
};

/**
 * Determines if a specific reference points to a step outside
 * of the current choice or branch context.
 */
const isExternalReference = (
  ref: { stepId: string },
  choiceStepId: string,
  branchStepIds: Set<string>
): boolean => {
  // If the reference is from within the same branch, that's expected
  if (branchStepIds.has(ref.stepId)) {
    return false;
  }
  // If the reference is from the choice step itself, we'll handle it specially in checkHasExternalReferences
  // to check if it's from a different choice
  if (ref.stepId === choiceStepId) {
    return false; // Will be handled in checkHasExternalReferences
  }
  // Otherwise, it's an external reference
  return true;
};

/**
 * Checks if a step in a choice branch is referenced from outside the branch
 * Returns true if the step has external references (not from the choice step or within the same branch)
 * When checking a branch, references from other choices in the same choice step are considered external.
 */
const checkHasExternalReferences = (
  config: WorkflowConfigurationType,
  stepId: string,
  choiceStepId: string,
  branchStepIds: Set<string>,
  currentChoiceNext?: string
): boolean => {
  const allSteps = getAllWorkflowSteps(config.steps);
  const references = findAllReferencesToStep(
    config as unknown as Draft<WorkflowConfigurationType>,
    stepId
  );

  if (references.viaStart) {
    return true;
  }

  // Check references via next property
  const hasExternalReferencesViaNext = references.viaNext.some((ref) =>
    isExternalReference(ref, choiceStepId, branchStepIds)
  );

  // Check references via choices - if the reference is from the choice step being deleted,
  // we need to check if it's from a different choice (which would be external to this branch)
  const hasExternalReferencesViaChoices = references.viaChoices.some((ref) => {
    // If the reference is from the choice step itself, check if it's from a different choice
    if (ref.stepId === choiceStepId) {
      const choiceStep = allSteps[choiceStepId];
      if (choiceStep && isChoiceStep(choiceStep)) {
        const task = choiceStep.task as
          | ChoiceTask
          | FormChoiceAction
          | VariableChoiceAction;
        // Check if this step is referenced from a choice other than the current branch's choice
        // If currentChoiceNext is provided, we're checking a specific branch
        if (currentChoiceNext !== undefined) {
          const isReferencedFromOtherChoice = task.choices.some(
            (choice) =>
              choice.next === stepId && choice.next !== currentChoiceNext
          );
          return isReferencedFromOtherChoice;
        }
        // If no currentChoiceNext, any reference from the choice step is expected
        return false;
      }
      return false;
    }
    // Otherwise, check if it's external
    return isExternalReference(ref, choiceStepId, branchStepIds);
  });

  const hasExternalReferencesViaAssessment = references.viaAssessment.some(
    (ref) => isExternalReference(ref, choiceStepId, branchStepIds)
  );

  if (
    hasExternalReferencesViaNext ||
    hasExternalReferencesViaChoices ||
    hasExternalReferencesViaAssessment
  )
    return true;

  for (const ref of references.viaBranchStart) {
    // Check if the parallel step containing this branch is within our branch
    const parallelStep = allSteps[ref.parallelStepId];
    if (parallelStep && branchStepIds.has(ref.parallelStepId)) {
      continue;
    }
    // Otherwise, it's an external reference
    return true;
  }

  return false;
};

/**
 * Main orchestration function for deleting a step from the workflow
 */
export const deleteStepFromWorkflow = (
  draft: Draft<WorkflowConfigurationType>,
  stepIdToDelete: string
): void => {
  const allSteps = getAllWorkflowSteps(draft.steps);
  const step = allSteps[stepIdToDelete];

  if (!step) {
    return;
  }

  const location = findStepLocation(draft, stepIdToDelete);
  const references = findAllReferencesToStep(draft, stepIdToDelete);

  if (location.branchInfo) {
    deleteStepFromBranch(
      draft,
      stepIdToDelete,
      step,
      location.branchInfo,
      references
    );
  } else if (isChoiceStep(step)) {
    deleteChoiceStep(draft, stepIdToDelete, step, location, references);
  } else if (isParallelStep(step)) {
    deleteParallelStep(
      draft,
      stepIdToDelete,
      step as Draft<WorkflowParallelTasksType>,
      location,
      references
    );
  } else {
    deleteRegularStep(draft, stepIdToDelete, step, location, references);
  }
};
